/**
 * SAS-free resource URLs for "Copy URL" (spec §5.2 / §6.3). Built from the
 * connection's endpoints through `serviceEndpointFor`; the credential never
 * lives in `Connection.server` / `azureStorageEndpoints`, and a query string
 * is stripped defensively so no `sig=` can ever reach the clipboard.
 */
import type { Connection } from '../../../../types/electron';
import type { StorageResourceRef } from '../../../../types/azureStorage';
import { storageServiceOf } from '../../../../types/azureStorage';
import { normalizeStorageAccountInput, serviceEndpointFor } from '../../../../core/engines/azureStorageUrl';

function encodePath(path: string): string {
  return path.split('/').map((segment) => encodeURIComponent(segment)).join('/');
}

function stripQuery(url: string): string {
  const index = url.indexOf('?');
  return index === -1 ? url : url.slice(0, index);
}

function resourcePathOf(ref: StorageResourceRef): string | undefined {
  switch (ref.kind) {
    case 'account':
    case 'service':
      return '';
    case 'container':
      return encodePath(ref.container);
    case 'blobPrefix':
      return encodePath(`${ref.container}/${ref.prefix}`);
    case 'blob':
      return encodePath(`${ref.container}/${ref.name}`);
    case 'table':
    case 'entity':
      return encodePath(ref.table);
    case 'queue':
    case 'message':
      return encodePath(ref.queue);
    case 'share':
      return encodePath(ref.share);
    case 'directory':
    case 'file':
      return encodePath(ref.path ? `${ref.share}/${ref.path}` : ref.share);
    case 'hnsPath':
      return encodePath(ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem);
  }
}

export function storageResourceUrl(connection: Pick<Connection, 'server' | 'azureStorageEndpoints' | 'azureStorageEmulator'>, ref: StorageResourceRef): string | undefined {
  const input = normalizeStorageAccountInput(connection.server ?? '');
  const service = ref.kind === 'hnsPath' ? 'dfs' : storageServiceOf(ref);
  if (!service) return undefined;
  const endpoint = serviceEndpointFor(
    { accountName: input.accountName, endpoints: connection.azureStorageEndpoints, emulator: connection.azureStorageEmulator },
    service,
  ) ?? (input.endpoint && service === 'blob' ? input.endpoint : undefined);
  if (!endpoint) return undefined;
  const base = stripQuery(endpoint).replace(/\/+$/, '');
  const path = resourcePathOf(ref);
  const url = path ? `${base}/${path}` : base;
  // S7-06: a share snapshot is addressed by its `sharesnapshot` query (the URL stays SAS-free; blob refs are unchanged).
  const snapshot = (ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file') ? ref.snapshot : undefined;
  return snapshot ? `${url}?sharesnapshot=${encodeURIComponent(snapshot)}` : url;
}
